import { type StateObservable } from 'redux-observable';
import { concat, type Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, timeout } from 'rxjs/operators';

import { type Actions as RootActions } from '../actions';
import { type AppState } from '../reducer';
import { safeWithLatestFromState } from '../utils/safeWithLatestFrom';

import { actionCreators, ActionTypes } from './actions';
import { getAvailablePlans, getOrders, postExecutedPlan, postLaunchOrderEndDate, postRejectedPlan } from './api';
import { OrderSource } from './model';

export const availablePlansFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_AVAILABLE_PLANS }> =>
        action.type === ActionTypes.FETCH_AVAILABLE_PLANS,
    ),
    mergeMap(({ clearSelectedPlan, absoluteMode }) =>
      getAvailablePlans(absoluteMode).pipe(
        timeout(15000), // 15 second timeout
        map((response) => actionCreators.fetchAvailablePlansFulfilled(clearSelectedPlan, absoluteMode, response)),
        catchError((error) => {
          // Create a more user-friendly error message for timeouts
          const isTimeout = error.name === 'TimeoutError';
          const friendlyError = isTimeout
            ? new Error('Connection timeout - please check your VPN connection and try again')
            : error;
          return of(actionCreators.fetchAvailablePlansFailed(clearSelectedPlan, friendlyError));
        }),
      ),
    ),
  );

export const selectPlanGet = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.GET_SELECTED_PLAN_ID }> =>
        action.type === ActionTypes.GET_SELECTED_PLAN_ID,
    ),
    mergeMap(({ accountID }) => of(actionCreators.fetchLaunchOrders(accountID, OrderSource.Launch))),
  );

export const selectedPlanRefresh = (action$: Observable<RootActions>, state$: StateObservable<AppState>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_AVAILABLE_PLANS_FULFILLED }> =>
        action.type === ActionTypes.FETCH_AVAILABLE_PLANS_FULFILLED,
    ),
    safeWithLatestFromState(state$),
    filter(([action, state]) => !action.clearSelectedPlan && state.provisioning.selectedPlan.accountID !== null),
    map(([_, state]) => actionCreators.getSelectedPlanID(`${state.provisioning.selectedPlan.accountID}`)),
  );

export const launchOrdersFetch = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.FETCH_LAUNCH_ORDERS }> =>
        action.type === ActionTypes.FETCH_LAUNCH_ORDERS,
    ),
    mergeMap(({ accountID, source }) =>
      getOrders({ accountID, source }).pipe(
        map((response) => actionCreators.fetchLaunchOrdersFulfilled(accountID, response)),
        catchError((error) => of(actionCreators.fetchLaunchOrdersFailed(accountID, error))),
      ),
    ),
  );

export const launchOrderEndDateSet = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SET_LAUNCH_ORDER_END_DATE }> =>
        action.type === ActionTypes.SET_LAUNCH_ORDER_END_DATE,
    ),
    mergeMap(({ accountID, orderID, endDate, absoluteMode }) =>
      postLaunchOrderEndDate(orderID, endDate).pipe(
        mergeMap((response) =>
          concat(
            of(actionCreators.setLaunchOrderEndDateFulfilled(accountID, orderID, endDate, response)),
            of(actionCreators.fetchAvailablePlans(false, absoluteMode)),
          ),
        ),
        catchError((error) => of(actionCreators.setLaunchOrderEndDateFailed(accountID, orderID, endDate, error))),
      ),
    ),
  );

export const executedPlanSet = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SET_EXECUTED_PLAN }> =>
        action.type === ActionTypes.SET_EXECUTED_PLAN,
    ),
    mergeMap(({ accountID, inputsHash, skipEmailNotification, userEmail, absoluteMode }) =>
      postExecutedPlan(accountID, inputsHash, absoluteMode, skipEmailNotification, userEmail).pipe(
        mergeMap((response) =>
          concat(
            of(actionCreators.setExecutedPlanFulfilled(accountID, response)),
            of(actionCreators.fetchAvailablePlans(true, absoluteMode)),
          ),
        ),
        catchError((error) => of(actionCreators.setExecutedPlanFailed(accountID, error))),
      ),
    ),
  );

export const rejectedPlanSet = (action$: Observable<RootActions>) =>
  action$.pipe(
    filter(
      (action): action is Extract<RootActions, { type: ActionTypes.SET_REJECTED_PLAN }> =>
        action.type === ActionTypes.SET_REJECTED_PLAN,
    ),
    mergeMap(({ accountID, inputsHash, absoluteMode, userEmail, comment }) =>
      postRejectedPlan(accountID, inputsHash, absoluteMode, userEmail, comment).pipe(
        mergeMap((response) =>
          concat(
            of(actionCreators.setRejectedPlanFulfilled(accountID, response)),
            of(actionCreators.fetchAvailablePlans(true, absoluteMode)),
          ),
        ),
        catchError((error) => of(actionCreators.setRejectedPlanFailed(accountID, error))),
      ),
    ),
  );
